<template>
  <div>
    <b-card>
      <b-card-header>
        <div class="d-flex justify-content-between align-items-between w-100">
          <h4>التراخيص والموافقات</h4>
          <b-button
            v-b-tooltip.hover.top="' جديدة تراخيص/موافقة إضافة'"
            variant="primary"
            class="btn-sm"
            @click="openLicenseModal"
          >
            <feather-icon
              icon="FileTextIcon"
              size="16"
            />
          </b-button>
        </div>

      </b-card-header>
      <b-card-body>
        <!-- Empty State -->
        <div
          v-if="!projectLicenses || projectLicenses.length === 0"
          class="text-center py-5"
        >
          <feather-icon
            icon="FileIcon"
            size="50"
            class="text-muted mb-2"
          />
          <h5 class="text-muted">
            لا توجد تراخيص مسجلة
          </h5>
          <p class="text-muted mb-0">
            قم بإضافة الترخيص الأول للمشروع
          </p>
        </div>

        <!-- Licenses Grid -->
        <div
          v-else
          class="licenses-grid"
        >
          <b-card
            v-for="license in projectLicenses"
            :key="license.id"
            class="license-card mb-3"
            :class="{ 'primary-license': license.is_primary }"
          >
            <b-card-body>
              <!-- Header with License Number and Status -->
              <div class="d-flex justify-content-between align-items-start mb-3">
                <div>
                  <h5 class="mb-1">
                    {{ license.license_number }}
                    <b-badge
                      v-if="license.is_primary"
                      variant="light-primary"
                      class="mr-1"
                    >
                      أساسي
                    </b-badge>
                  </h5>
                  <b-badge
                    :variant="getStatusVariant(license.status)"
                    class="text-capitalize"
                  >
                    {{ license.status }}
                  </b-badge>
                </div>
                <div class="license-actions">
                  <b-button
                    v-b-tooltip.hover.top="'تعديل'"
                    variant="outline-primary"
                    size="sm"
                    class="btn-icon mr-1"
                    @click="editLicense(license)"
                  >
                    <feather-icon
                      icon="EditIcon"
                      size="14"
                    />
                  </b-button>
                  <b-button
                    v-b-tooltip.hover.top="'حذف'"
                    variant="outline-danger"
                    size="sm"
                    class="btn-icon "
                    @click="deleteLicense(license)"
                  >
                    <feather-icon
                      icon="TrashIcon"
                      size="14"
                    />
                  </b-button>
                </div>
              </div>

              <!-- License Details Grid -->
              <b-row class="license-details">
                <b-col
                  md="6"
                  class="mb-2"
                >
                  <div class="detail-item">
                    <feather-icon
                      icon="CalendarIcon"
                      size="16"
                      class="text-primary mr-1"
                    />
                    <span class="text-muted">تاريخ الإصدار:</span>
                    <strong class="mr-1">{{ formatDate(license.license_issue_date) }}</strong>
                  </div>
                </b-col>
                <b-col
                  md="6"
                  class="mb-2"
                >
                  <div class="detail-item">
                    <feather-icon
                      icon="CalendarIcon"
                      size="16"
                      class="text-warning mr-1"
                    />
                    <span class="text-muted">تاريخ الانتهاء:</span>
                    <strong class="mr-1">{{ formatDate(license.license_expiry_date) }}</strong>
                  </div>
                </b-col>
                <b-col
                  md="6"
                  class="mb-2"
                >
                  <div class="detail-item">
                    <feather-icon
                      icon="HomeIcon"
                      size="16"
                      class="text-success mr-1"
                    />
                    <span class="text-muted">الجهة المصدرة:</span>
                    <strong class="mr-1">{{ license.license_authority }}</strong>
                  </div>
                </b-col>
                <b-col
                  md="6"
                  class="mb-2"
                >
                  <div class="detail-item">
                    <feather-icon
                      icon="LayersIcon"
                      size="16"
                      class="text-info mr-1"
                    />
                    <span class="text-muted">نوع الترخيص:</span>
                    <strong class="mr-1">{{ license.license_type }}</strong>
                  </div>
                </b-col>
              </b-row>

              <!-- Project Specifications -->
              <b-card class="bg-light mt-3">
                <b-card-body class="py-2">
                  <h6 class="text-primary mb-2">
                    المواصفات المرخصة
                  </h6>
                  <b-row>
                    <b-col
                      md="4"
                      class="text-center"
                    >
                      <div class="spec-item">
                        <div class="spec-value text-primary">
                          {{ license.licensed_floors }}
                        </div>
                        <div class="spec-label">
                          عدد الطوابق
                        </div>
                      </div>
                    </b-col>
                    <b-col
                      md="4"
                      class="text-center"
                    >
                      <div class="spec-item">
                        <div class="spec-value text-success">
                          {{ license.licensed_units }}
                        </div>
                        <div class="spec-label">
                          عدد الوحدات
                        </div>
                      </div>
                    </b-col>
                    <b-col
                      md="4"
                      class="text-center"
                    >
                      <div class="spec-item">
                        <div class="spec-value text-info">
                          {{ license.licensed_total_area }} م²
                        </div>
                        <div class="spec-label">
                          المساحة الإجمالية
                        </div>
                      </div>
                    </b-col>
                  </b-row>
                </b-card-body>
              </b-card>

              <!-- Usage and Description -->
              <b-row class="mt-3">
                <b-col md="6">
                  <div class="info-item">
                    <strong class="text-muted">الاستخدام المرخص:</strong>
                    <p class="mb-0">
                      {{ license.licensed_usage }}
                    </p>
                  </div>
                </b-col>
                <b-col md="6">
                  <div class="info-item">
                    <strong class="text-muted">وصف الأعمال:</strong>
                    <p class="mb-0">
                      {{ license.licensed_work_description }}
                    </p>
                  </div>
                </b-col>
              </b-row>

              <!-- Notes -->
              <div
                v-if="license.notes"
                class="mt-3"
              >
                <strong class="text-muted">ملاحظات:</strong>
                <p class="mb-0 text-secondary">
                  {{ license.notes }}
                </p>
              </div>

              <!-- Footer with Dates -->
              <div class="d-flex justify-content-between align-items-center mt-3 pt-2 border-top">
                <small class="text-muted">
                  تم الإنشاء: {{ formatDateTime(license.created_at) }}
                </small>
                <small class="text-muted">
                  آخر تحديث: {{ formatDateTime(license.updated_at) }}
                </small>
              </div>
            </b-card-body>
          </b-card>
        </div>
      </b-card-body>

    </b-card>
    <b-modal
      id="license-modal"
      v-model="licenseModal"
      :title="license.id ? 'تعديل ترخيص / موافقة' : 'اضف ترخيص / موافقة جديدة'"
      size="xl"
      hide-footer
      no-close-on-backdrop
      hide-header-close
      centered
    >
      <div>

        <b-form
          class="chat-app-form p-2"
          @submit.prevent="submitLicenses"
        >
          <validation-observer
            ref="licenseForm"
            #default="{invalid}"
            class="w-100"
          >

            <!-- Project Specs -->
            <b-row>
              <b-col
                md="4"
                class="mt-1"
              >
                <validation-provider
                  #default="{ errors }"
                  name="license_number"
                  vid="license_number"
                  rules="required"
                >
                  <b-form-group
                    label="رقم الترخيص"
                    :label-for="`license_number`"
                  >
                    <b-form-input
                      :id="`license_number`"
                      v-model="license.license_number"
                      placeholder="BP-2024-001234"
                    />
                  </b-form-group>
                  <small class="text-danger float-left">{{ errors[0] }}</small>
                </validation-provider>
              </b-col>
              <b-col
                md="4"
                class="mt-1"
              >
                <validation-provider
                  #default="{ errors }"
                  name="license_type"
                  vid="license_type"
                  rules="required"
                >

                  <b-form-group
                    label="نوع الترخيص"
                    :label-for="`license_type`"
                  >
                    <b-form-input
                      :id="`license_type`"
                      v-model="license.license_type"
                      placeholder="Building Permit"
                    />
                  </b-form-group>
                  <small class="text-danger float-left">{{ errors[0] }}</small>
                </validation-provider>
              </b-col>
              <b-col
                md="4"
                class="mt-1"
              >
                <validation-provider
                  #default="{ errors }"
                  name="license_authority"
                  vid="license_authority"
                  rules="required"
                >
                  <b-form-group
                    label="الجهة المصدرة"
                    :label-for="`license_authority`"
                  >
                    <b-form-input
                      :id="`license_authority`"
                      v-model="license.license_authority"
                    />
                  </b-form-group>
                  <small class="text-danger float-left">{{ errors[0] }}</small>
                </validation-provider>
              </b-col>
              <b-col
                md="3"
                class="mt-1"
              >
                <validation-provider
                  #default="{ errors }"
                  name="license_issue_date"
                  vid="license_issue_date"
                  rules="required"
                >
                  <b-form-group
                    label="تاريخ الإصدار"
                    :label-for="`license_issue_date`"
                  >
                    <b-form-input
                      :id="`license_issue_date`"
                      v-model="license.license_issue_date"
                      type="date"
                    />
                  </b-form-group>
                  <small class="text-danger float-left">{{ errors[0] }}</small>
                </validation-provider>
              </b-col>
              <b-col
                md="3"
                class="mt-1"
              >
                <validation-provider
                  #default="{ errors }"
                  name="license_expiry_date"
                  vid="license_expiry_date"
                  rules="required"
                >
                  <b-form-group
                    label="تاريخ الانتهاء"
                    :label-for="`license_expiry_date`"
                  >
                    <b-form-input
                      :id="`license_expiry_date`"
                      v-model="license.license_expiry_date"
                      type="date"
                    />
                  </b-form-group>
                  <small class="text-danger float-left">{{ errors[0] }}</small>
                </validation-provider>
              </b-col>
              <b-col
                md="3"
                class="mt-1"
              >
                <validation-provider
                  #default="{ errors }"
                  name="license_status"
                  vid="license_status"
                  rules="required"
                >
                  <b-form-group
                    label="الحالة"
                    :label-for="`license_status`"
                  >
                    <v-select
                      :id="`license_status`"
                      v-model="license.status"
                      :options="licenseStatuses"
                      :reduce="val => val.value"
                      :get-option-label="option => option.label_ar"
                    />
                  </b-form-group>
                  <small class="text-danger float-left">{{ errors[0] }}</small>
                </validation-provider>
              </b-col>
              <b-col
                md="3"
                class="mt-1"
              >
                <validation-provider
                  #default="{ errors }"
                  name="is_primary"
                  vid="is_primary"
                  rules="required"
                >
                  <b-form-group
                    label="ترخيص رئيسي"
                    :label-for="`is_primary`"
                  >
                    <b-form-checkbox
                      :id="`is_primary`"
                      v-model="license.is_primary"
                      class="mt-1"
                    />
                  </b-form-group>
                  <small class="text-danger float-left">{{ errors[0] }}</small>
                </validation-provider>
              </b-col>
              <b-col
                md="3"
                class="mt-1"
              >
                <validation-provider
                  #default="{ errors }"
                  name="licensed_floors"
                  vid="licensed_floors"
                  rules="required"
                >
                  <b-form-group
                    label="الأدوار المرخصة"
                    :label-for="`licensed_floors`"
                  >
                    <b-form-input
                      :id="`licensed_floors`"
                      v-model="license.licensed_floors"
                      type="number"
                    />
                  </b-form-group>
                  <small class="text-danger float-left">{{ errors[0] }}</small>
                </validation-provider>
              </b-col>
              <b-col
                md="3"
                class="mt-1"
              >
                <validation-provider
                  #default="{ errors }"
                  name="licensed_units"
                  vid="licensed_units"
                  rules="required"
                >
                  <b-form-group
                    label="الوحدات المرخصة"
                    :label-for="`licensed_units`"
                  >
                    <b-form-input
                      :id="`licensed_units`"
                      v-model="license.licensed_units"
                      type="number"
                    />
                  </b-form-group>
                  <small class="text-danger float-left">{{ errors[0] }}</small>
                </validation-provider>
              </b-col>
              <b-col
                md="3"
                class="mt-1"
              >
                <validation-provider
                  #default="{ errors }"
                  name="licensed_total_area"
                  vid="licensed_total_area"
                  rules="required"
                >
                  <b-form-group
                    label="المساحة الإجمالية (م²)"
                    :label-for="`licensed_total_area`"
                  >
                    <b-form-input
                      :id="`licensed_total_area`"
                      v-model="license.licensed_total_area"
                      type="number"
                      step="0.01"
                    />
                  </b-form-group>
                  <small class="text-danger float-left">{{ errors[0] }}</small>
                </validation-provider>
              </b-col>
              <b-col
                md="3"
                class="mt-1"
              >
                <validation-provider
                  #default="{ errors }"
                  name="licensed_usage"
                  vid="licensed_usage"
                  rules="required"
                >
                  <b-form-group
                    label="الاستخدام المرخص"
                    :label-for="`licensed_usage`"
                  >
                    <b-form-input
                      :id="`licensed_usage`"
                      v-model="license.licensed_usage"
                      placeholder="residential/commercial"
                    />
                  </b-form-group>
                  <small class="text-danger float-left">{{ errors[0] }}</small>
                </validation-provider>
              </b-col>
              <b-col
                md="12"
                class="mt-1"
              >
                <validation-provider
                  #default="{ errors }"
                  name="licensed_work_desc"
                  vid="licensed_work_desc"
                >
                  <b-form-group
                    label="وصف العمل المرخص"
                    :label-for="`licensed_work_desc`"
                  >
                    <b-form-textarea
                      :id="`licensed_work_desc`"
                      v-model="license.licensed_work_description"
                      rows="2"
                    />
                  </b-form-group>
                  <small class="text-danger float-left">{{ errors[0] }}</small>
                </validation-provider>
              </b-col>
              <b-col
                md="12"
                class="mt-1"
              >
                <validation-provider
                  #default="{ errors }"
                  name="license_notes"
                  vid="license_notes"
                >
                  <b-form-group
                    label="ملاحظات"
                    :label-for="`license_notes`"
                  >
                    <b-form-textarea
                      :id="`license_notes`"
                      v-model="license.notes"
                      rows="2"
                    />
                  </b-form-group>
                  <small class="text-danger float-left">{{ errors[0] }}</small>
                </validation-provider>
              </b-col>
            </b-row>

          </validation-observer>
        </b-form>
      </div>
      <div class="d-flex justify-content-end">
        <b-button
          variant="primary"
          class="rounded-pill mt-2"
          type="submit"
          @click="submitLicenses"
        >
          حفظ
        </b-button>
        <b-button
          class="rounded-pill mt-2 ml-1"
          @click="licenseModal = false"
        >
          الغاء
        </b-button>
      </div>
    </b-modal>

  </div>
</template>
<script>
import {
  BTable,
  BButton,
  BFormCheckbox,
  BCard,
  BRow,
  BCol,
  BBadge,
  BCardText,
  BAvatar,
  BLink,
  VBTooltip,
  BSpinner,
  BPagination,
  BFormInput,
  BInputGroup,
  BTransition,
  BForm,
  BTabs,
  BTab,
  BFormTextarea,
  BFormGroup,
  BCardBody, // Added explicitly for clarity
  BCardHeader, // Added explicitly for clarity
  BCardFooter, // Added explicitly for clarity
  BModal,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { mapGetters } from 'vuex'
import vSelect from 'vue-select'
import { required } from '@validations'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import Button from '@/views/components/button/Button.vue'

export default {
  name: 'LicensesComponent',
  components: {
    ValidationProvider,
    ValidationObserver,
    BCard,
    BCardHeader,
    BCardBody,
    BCardFooter,
    BFormTextarea,
    BFormCheckbox,
    vSelect,
    BTable,
    BButton,
    BBadge,
    BSpinner,
    BTabs,
    BTab,
    BRow,
    BCol,
    BFormInput,
    BInputGroup,
    BForm,
    BFormGroup,
    BModal,
    Button,
  },
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },
  props: {
    projectId: {
      type: Number,
      required: true,
    },
    projectLicenses: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      licenseModal: false,
      license: {
        id: null,
        license_number: null,
        license_issue_date: null,
        license_expiry_date: null,
        license_authority: null,
        license_type: null,
        licensed_floors: null,
        licensed_units: null,
        licensed_total_area: null,
        licensed_usage: null,
        licensed_work_description: null,
        is_primary: true,
        status: 'active',
        notes: null,
      },
      licenseStatuses: [
        { value: 'active', label: 'Active', label_ar: 'نشط' },
        { value: 'expired', label: 'Expired', label_ar: 'منتهي' },
        { value: 'revoked', label: 'Revoked', label_ar: 'ملغي' },
        { value: 'pending', label: 'Pending', label_ar: 'معلق' },
      ],
    }
  },
  methods: {
    openLicenseModal() {
      this.resetLicenseForm()
      this.licenseModal = true
    },
    editLicense(license) {
      // Populate form with license data
      this.license = {
        id: license.id,
        license_number: license.license_number,
        license_issue_date: license.license_issue_date,
        license_expiry_date: license.license_expiry_date,
        license_authority: license.license_authority,
        license_type: license.license_type,
        licensed_floors: license.licensed_floors,
        licensed_units: license.licensed_units,
        licensed_total_area: license.licensed_total_area,
        licensed_usage: license.licensed_usage,
        licensed_work_description: license.licensed_work_description,
        is_primary: license.is_primary,
        status: license.status,
        notes: license.notes,
      }
      this.licenseModal = true
    },
    resetLicenseForm() {
      this.license = {
        id: null,
        license_number: null,
        license_issue_date: null,
        license_expiry_date: null,
        license_authority: null,
        license_type: null,
        licensed_floors: null,
        licensed_units: null,
        licensed_total_area: null,
        licensed_usage: null,
        licensed_work_description: null,
        is_primary: true,
        status: 'active',
        notes: null,
      }
      // Reset validation
      if (this.$refs.licenseForm) {
        this.$refs.licenseForm.reset()
      }
    },
    submitLicenses() {
      this.$refs.licenseForm.validate().then(success => {
        if (success) {
          const payload = { query: { ...this.license } }
          if (this.license.id) {
            // update existing license
            payload.query.id = this.license.id
            payload.id = this.projectId
            this.$store.dispatch('projects/putLicense', payload).then(res => {
              this.licenseModal = false
              this.resetLicenseForm()
              this.$swal({
                icon: 'success',
                title: `<h4>${res.message}</h4>`,
                showConfirmButton: true,
                confirmButtonColor: '#06284D',
                allowOutsideClick: true,
              })
              // Refresh project data to get updated licenses
              this.$store.dispatch('projects/getProject', { id: this.projectId })
            }).catch(error => {
              if (error.response) {
                this.$swal({
                  icon: 'error',
                  title: `<h4>${error.response.data.message}</h4>`,
                  showConfirmButton: true,
                  confirmButtonColor: '#06284D',
                  allowOutsideClick: true,
                })
              }
            })
          } else {
            // store new license
            payload.id = this.projectId
            this.$store.dispatch('projects/putLicense', payload).then(res => {
              this.licenseModal = false
              this.resetLicenseForm()

              this.$swal({
                icon: 'success',
                title: `<h4>${res.message}</h4>`,
                showConfirmButton: true,
                confirmButtonColor: '#06284D',
                allowOutsideClick: true,
              })
              // Refresh project data to get updated licenses
              this.$store.dispatch('projects/getProject', { id: this.projectId })
            }).catch(error => {
              if (error.response) {
                this.$swal({
                  icon: 'error',
                  title: `<h4>${error.response.data.message}</h4>`,
                  showConfirmButton: true,
                  confirmButtonColor: '#06284D',
                  allowOutsideClick: true,
                })
              }
            })
          }
        }
      })
    },

    getStatusVariant(status) {
      const variants = {
        active: 'success',
        pending: 'warning',
        expired: 'danger',
        draft: 'secondary',
      }
      return variants[status] || 'secondary'
    },

    formatDate(dateString) {
      if (!dateString) return 'غير محدد'
      const date = new Date(dateString)
      return date.toLocaleDateString('ar-EG')
    },

    formatDateTime(dateTimeString) {
      if (!dateTimeString) return 'غير محدد'
      const date = new Date(dateTimeString)
      return date.toLocaleString('ar-EG')
    },
    deleteLicense(license) {
      this.$bvModal.msgBoxConfirm(`هل أنت متأكد من حذف الترخيص ${license.license_number}؟`, {
        title: 'تأكيد الحذف',
        okVariant: 'danger',
        okTitle: 'نعم، احذف',
        cancelTitle: 'إلغاء',
        centered: true,
      }).then(value => {
        if (value) {
          // Call delete API
          const payload = {
            id: this.projectId,
            query: { license_id: license.id },
          }
          this.$store.dispatch('projects/deleteLicense', payload).then(res => {
            this.$swal({
              icon: 'success',
              title: `<h4>${res.message || 'تم حذف الترخيص بنجاح'}</h4>`,
              showConfirmButton: true,
              confirmButtonColor: '#06284D',
              allowOutsideClick: true,
            })
            // Refresh project data to get updated licenses
            this.$store.dispatch('projects/getProject', { id: this.projectId })
          }).catch(error => {
            if (error.response) {
              this.$swal({
                icon: 'error',
                title: `<h4>${error.response.data.message || 'حدث خطأ أثناء حذف الترخيص'}</h4>`,
                showConfirmButton: true,
                confirmButtonColor: '#06284D',
                allowOutsideClick: true,
              })
            }
          })
        }
      })
    },
  },
}
</script>
